import { FC, useEffect, useRef } from "react";
import { PdfHandler } from "../../../middleware/handlers/pdf-handler";

interface PdfPagesMenuProps {
  pagesPanelReveal: boolean;
  pageNumber: number;
  pageCount: number;
  rotation: number;
  sheetNumbers: { [pageNumber: number]: string };
}

interface PdfThumbnailProps {
  pageNumber: number;
  sheetNumber: string | undefined;
  isCurrent: boolean;
  listRef: React.RefObject<HTMLDivElement | null>;
}

// Drawn only once it scrolls near the list's view, so a 300-sheet set does not
// render 300 pages the moment the panel opens.
const PdfThumbnail: FC<PdfThumbnailProps> = ({
  pageNumber,
  sheetNumber,
  isCurrent,
  listRef,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const item = itemRef.current;
    if (!item) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          if (canvasRef.current) {
            PdfHandler.RenderThumbnail(pageNumber, canvasRef.current).catch(
              (error) => {
                console.error("Error drawing page thumbnail:", error);
              },
            );
          }
        }
      },
      { root: listRef.current, rootMargin: "400px 0px" },
    );
    observer.observe(item);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isCurrent) {
      itemRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [isCurrent]);

  return (
    <div
      ref={itemRef}
      className={
        isCurrent ? "pdfThumbnail pdfThumbnailSelected" : "pdfThumbnail"
      }
      onClick={() => {
        PdfHandler.GoToPage(pageNumber);
      }}
    >
      <canvas ref={canvasRef} />
      <span>{sheetNumber ? `${sheetNumber} · ${pageNumber}` : pageNumber}</span>
    </div>
  );
};

export const PdfPagesMenu: FC<PdfPagesMenuProps> = ({
  pagesPanelReveal,
  pageNumber,
  pageCount,
  rotation,
  sheetNumbers,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  if (!pagesPanelReveal) return null;

  return (
    <div className="viewerToolPanel">
      <div className="viewerToolPanelHeader">
        <span className="viewerToolPanelLabel">PAGES</span>
        <span className="viewerToolPanelName">
          {pageCount === 1 ? "1 page" : `${pageCount} pages`}
        </span>
      </div>
      <div className="pdfThumbnailList" ref={listRef}>
        {Array.from({ length: pageCount }, (_, index) => (
          // keyed on rotation too, so turning the pages redraws them
          <PdfThumbnail
            key={`${index + 1}-${rotation}`}
            pageNumber={index + 1}
            sheetNumber={sheetNumbers[index + 1]}
            isCurrent={index + 1 === pageNumber}
            listRef={listRef}
          />
        ))}
      </div>
    </div>
  );
};

export default PdfPagesMenu;
